import { uniqBy } from "lodash";
import { zGuard } from "../zod/utils.js";

import {
  EffortLevel,
  ModelSpeed,
  getEffortDisplayLabel,
} from "./effortLevel.js";
import { Model, ModelLiteral } from "./Model.js";
import { ModelFeatureMap } from "./ModelFeatures.js";

/**
 * Resolve a model name to an active model for GraphQL and runtime use.
 * Returns null for values that are not recognized active Model members.
 */
export function resolveActiveModelName(
  model: string | null | undefined,
): Model | null {
  // Some historical threads may still have model set to null.
  // TODO(@zkirby): clean up this logic after >3 months when historical
  // threads have been gc'd.
  if (model == null) return null;
  return zGuard(ModelLiteral, model) ? model : null;
}

/**
 * Find the matching, currently-usable picker option for a model name. Returns
 * `undefined` when `model` is null/"auto" or when the model is absent or
 * disabled in `options`.
 */
export function findAvailableModelOption<
  T extends { model: string | null; disabled: boolean },
>(options: readonly T[], model: Model | null): T | undefined {
  if (model == null) {
    return undefined;
  }
  return options.find((opt) => opt.model === model && !opt.disabled);
}

export interface ModelSelection {
  model: Model | null;
  effortLevel: EffortLevel | null;
  speed: ModelSpeed | null;
}

export const AUTO_MODEL_SELECTION: ModelSelection = {
  model: null,
  effortLevel: null,
  speed: null,
};

/**
 * Resolve a stored/selected (model, effort, speed) against the org's currently
 * available picker options, mirroring the server's `resolveModelSelection`:
 * returns "auto" (all-null) unless the model is available, the effort is
 * supported, and — for fast — a fast variant exists for that effort (otherwise
 * the speed drops to standard). Shared by the org default-model setting row, the
 * prompt-bar seeding hook, and anywhere else a stored selection is validated on
 * read so the answer never diverges.
 */
export function resolveAvailableModelSelection<
  T extends {
    model: string | null;
    disabled: boolean;
    supportedEfforts: readonly EffortLevel[];
    supportedFastEfforts?: readonly EffortLevel[] | null;
  },
>(selection: ModelSelection, options: readonly T[]): ModelSelection {
  const available = findAvailableModelOption(options, selection.model);
  if (
    available == null ||
    selection.effortLevel == null ||
    !available.supportedEfforts.includes(selection.effortLevel)
  ) {
    return AUTO_MODEL_SELECTION;
  }
  const shouldUseFast =
    selection.speed === "fast" &&
    (available.supportedFastEfforts ?? []).includes(selection.effortLevel);
  return {
    model: selection.model,
    effortLevel: selection.effortLevel,
    speed: shouldUseFast ? "fast" : null,
  };
}

export type ThreadModelFields = {
  auto: boolean | null;
  model: string | null;
  effortLevel: string | null;
};

/** The model the user explicitly picked or 'null' if for deprecated and 'auto' models */
export const threadPickerModel = (
  thread: Partial<ThreadModelFields>,
): Model | null => (thread.auto ? null : resolveActiveModelName(thread.model));

/**
 * Whether the thread's model is Auto rather than an explicit picker selection.
 *
 * True for Auto threads, legacy threads with no persisted model, and threads
 * whose persisted model is no longer a recognized ModelName.
 */
export const threadModelIsAuto = (
  thread: Partial<ThreadModelFields>,
): boolean =>
  thread.auto === true || resolveActiveModelName(thread.model) == null;

/**
 * Label for the model an eval run/attempt actually ran on.
 * - auto: "Auto (Fable 5, High)"
 * - explicit/pinned: "Fable 5 (High)"
 */
export function formatModelLabel({
  auto,
  effortLevel,
  model,
}: ThreadModelFields): string {
  const resolved = resolveActiveModelName(model);
  const modelLabel = resolved ? ModelFeatureMap[resolved].label : model;
  if (modelLabel == null) {
    return "Auto";
  }
  const effortLabel = getEffortDisplayLabel(effortLevel);
  if (auto) {
    return effortLabel == null
      ? `Auto (${modelLabel})`
      : `Auto (${modelLabel}, ${effortLabel})`;
  }
  return effortLabel == null ? modelLabel : `${modelLabel} (${effortLabel})`;
}

/**
 * Whether the model an eval attempt ran on deviates from the suite's pinned
 * selection.
 */
export function evalAttemptDeviatesFromPinnedModel(
  effectiveModel: ThreadModelFields,
  pinnedSelection: { model: string; effortLevel?: string | null },
): boolean {
  return (
    effectiveModel.auto ||
    effectiveModel.model !== pinnedSelection.model ||
    (effectiveModel.effortLevel ?? null) !==
      (pinnedSelection.effortLevel ?? null)
  );
}

export interface EvalSuiteModelLabel {
  /** The single label to render in a summary cell. */
  label: string;
  /** Distinct per-model labels, for a tooltip when {@link mixed}. */
  distinctLabels: readonly string[];
  /** Whether the run's attempts spanned more than one model. */
  mixed: boolean;
}

/**
 * Summary label for an eval suite run, or null when the run was left unpinned
 * and no attempt has recorded a model yet — each surface renders that absence
 * its own way.
 */
export function formatEvalSuiteModelLabel({
  effectiveModels,
  modelSelection,
}: {
  modelSelection:
    | { model: string; effortLevel?: string | null }
    | null
    | undefined;
  effectiveModels: readonly ThreadModelFields[];
}): EvalSuiteModelLabel | null {
  // A pinned suite reports its pin rather than what was recorded on the
  // threads: an unavailable pin fails the attempt rather than silently
  // downgrading, so no attempt ran on a different model. Attempts that failed
  // before starting still report the pin they were going to run on.
  if (modelSelection != null) {
    const label = formatModelLabel({
      auto: false,
      model: modelSelection.model,
      effortLevel: modelSelection.effortLevel ?? null,
    });
    return { label, distinctLabels: [label], mixed: false };
  }

  const distinctModels = uniqBy(effectiveModels, (model) =>
    // Dedupe on identity rather than on the rendered label, so a future label
    // change can't collapse two distinct models into one.
    JSON.stringify([model.auto, model.model, model.effortLevel ?? null]),
  );
  const [firstModel] = distinctModels;
  if (firstModel == null) {
    return null;
  }
  if (distinctModels.length === 1) {
    const label = formatModelLabel(firstModel);
    return { label, distinctLabels: [label], mixed: false };
  }

  const allAuto = distinctModels.every(({ auto }) => auto);
  return {
    label: allAuto ? "Auto (Mixed)" : "Mixed",
    distinctLabels: distinctModels.map(formatModelLabel),
    mixed: true,
  };
}
